import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  DataObject as JsonIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../services/api';
import { PageHeader, Loading } from '../components/Layout';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('income');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    // Set default dates to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select date range');
      return;
    }

    try {
      setLoading(true);
      let response;
      
      switch (reportType) {
        case 'income':
          response = await api.get('/api/reports/income', {
            params: { start_date: startDate, end_date: endDate }
          });
          break;
        case 'occupancy':
          response = await api.get('/api/reports/occupancy', {
            params: { start_date: startDate, end_date: endDate }
          });
          break;
        case 'unpaid':
          response = await api.get('/api/reports/unpaid');
          break;
        case 'expenses':
          response = await api.get('/api/reports/expenses', {
            params: { start_date: startDate, end_date: endDate }
          });
          break;
        default:
          break;
      }

      // Normalize the response data
      const data = response.data;
      const normalizedData = {
        ...data,
        total: data.total || data.total_income || data.total_unpaid || data.total_amount || data.total_expense || 0,
        count: data.count || data.items?.length || 0,
        items: data.items || [],
        period: data.period,
      };
      
      setReportData(normalizedData);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportMenuOpen = (event) => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportJSON = () => {
    const jsonData = JSON.stringify(reportData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported as JSON');
    handleExportMenuClose();
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add title
    doc.setFontSize(20);
    const title = `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`;
    doc.text(title, pageWidth / 2, 20, { align: 'center' });
    
    // Add period if available
    if (reportData.period) {
      doc.setFontSize(10);
      doc.text(
        `Period: ${reportData.period.start_date} to ${reportData.period.end_date}`,
        pageWidth / 2,
        28,
        { align: 'center' }
      );
    }
    
    // Add generated date
    doc.setFontSize(9);
    doc.text(
      `Generated: ${new Date().toLocaleString()}`,
      pageWidth / 2,
      35,
      { align: 'center' }
    );
    
    let yPos = 45;
    
    // Add summary information
    doc.setFontSize(14);
    doc.text('Summary', 14, yPos);
    yPos += 10;
    
    doc.setFontSize(11);
    const summaryData = [];
    
    if (reportData.total) {
      summaryData.push(['Total Amount', `KES ${reportData.total.toLocaleString()}`]);
    }
    if (reportData.count) {
      summaryData.push(['Items Count', reportData.count.toString()]);
    }
    
    // Add other summary fields
    Object.entries(reportData)
      .filter(([key]) => !['items', 'count', 'period', 'total'].includes(key))
      .forEach(([key, value]) => {
        if (typeof value !== 'object') {
          const label = key.replace(/_/g, ' ').toUpperCase();
          const displayValue = typeof value === 'number'
            ? (key.toLowerCase().includes('amount') || key.toLowerCase().includes('income') || key.toLowerCase().includes('expense')
              ? `KES ${value.toLocaleString()}`
              : value.toLocaleString())
            : String(value);
          summaryData.push([label, displayValue]);
        }
      });
    
    if (summaryData.length > 0) {
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: 14, right: 14 },
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Add items table if available
    if (reportData.items && reportData.items.length > 0) {
      doc.setFontSize(14);
      doc.text('Details', 14, yPos);
      yPos += 5;
      
      const headers = Object.keys(reportData.items[0]).map(key => 
        key.replace(/_/g, ' ').toUpperCase()
      );
      
      const rows = reportData.items.map(item => 
        Object.values(item).map(value => {
          if (typeof value === 'number' && value > 1000) {
            return value.toLocaleString();
          }
          return String(value);
        })
      );
      
      autoTable(doc, {
        startY: yPos,
        head: [headers],
        body: rows,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 },
      });
    }
    
    // Save the PDF
    doc.save(`${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Report exported as PDF');
    handleExportMenuClose();
  };

  return (
    <Box>
      <PageHeader
        title="Reports & Analytics"
        subtitle="Generate and view detailed property reports"
        breadcrumbs={[{ label: 'Reports' }]}
        action={
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportMenuOpen}
              disabled={!reportData}
            >
              Export Report
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={exportMenuOpen}
              onClose={handleExportMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleExportPDF}>
                <ListItemIcon>
                  <PdfIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Export as PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportJSON}>
                <ListItemIcon>
                  <JsonIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>Export as JSON</ListItemText>
              </MenuItem>
            </Menu>
          </>
        }
      />
      {/* Report Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Generate Report
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Report Type"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              <MenuItem value="income">Income Report</MenuItem>
              <MenuItem value="occupancy">Occupancy Report</MenuItem>
              <MenuItem value="unpaid">Unpaid Invoices</MenuItem>
              <MenuItem value="expenses">Expense Report</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGenerateReport}
              disabled={loading}
              sx={{ height: 56 }}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {/* Report Summary Cards */}
      {reportData && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="h6">
                        Total Amount
                      </Typography>
                      <Typography variant="h4">
                        KES {reportData.total?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="h6">
                        Items Count
                      </Typography>
                      <Typography variant="h4">
                        {reportData.count || reportData.items?.length || '0'}
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 48, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="h6">
                        Report Type
                      </Typography>
                      <Typography variant="h4">
                        {reportType.charAt(0).toUpperCase() + reportType.slice(1)}
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Report Details */}
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Report Details
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PdfIcon />}
                  onClick={handleExportPDF}
                  color="error"
                >
                  Download PDF
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<JsonIcon />}
                  onClick={handleExportJSON}
                >
                  Download JSON
                </Button>
              </Box>
            </Box>
            
            {/* Period Information */}
            {reportData.period && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Period:</strong> {reportData.period.start_date} to {reportData.period.end_date}
                </Typography>
              </Box>
            )}
            
            {reportData.items && reportData.items.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {Object.keys(reportData.items[0]).map((key) => (
                        <TableCell key={key}>
                          <strong>{key.replace(/_/g, ' ').toUpperCase()}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.items.map((item, index) => (
                      <TableRow key={index} hover>
                        {Object.entries(item).map(([key, value], i) => (
                          <TableCell key={i}>
                            {(() => {
                              // Format amount/payment fields with currency
                              if (key.toLowerCase().includes('amount') || key.toLowerCase().includes('paid')) {
                                return typeof value === 'number' ? `KES ${value.toLocaleString()}` : value;
                              }
                              // Format date fields
                              if (key.toLowerCase().includes('date') && value) {
                                try {
                                  return new Date(value).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  });
                                } catch {
                                  return String(value);
                                }
                              }
                              // Format payment method
                              if (key === 'payment_method' && value) {
                                return value.toUpperCase();
                              }
                              // Default: format numbers, show strings as-is
                              return typeof value === 'number' && value > 1000
                                ? value.toLocaleString()
                                : String(value);
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {Object.entries(reportData)
                    .filter(([key]) => !['items', 'count', 'period'].includes(key))
                    .map(([key, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {key.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="h6">
                            {typeof value === 'number' 
                              ? (key.toLowerCase().includes('amount') || key.toLowerCase().includes('income') || key.toLowerCase().includes('expense')
                                  ? `KES ${value.toLocaleString()}`
                                  : value.toLocaleString())
                              : typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                </Grid>
                {Object.keys(reportData).filter(k => !['items', 'count', 'period', 'total'].includes(k)).length === 0 && (
                  <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                    No detailed data available for this report
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </>
      )}
      {/* Empty State */}
      {!reportData && !loading && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <AssessmentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Report Generated
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Select a report type and date range, then click "Generate Report"
          </Typography>
        </Paper>
      )}
      {loading && <Loading message="Generating report..." />}
    </Box>
  );
};

export default Reports;
